import { reportError } from '@pypestream/utils';

/**
 * Reports a store failure the user was shown as a toast.
 *
 * The three writes this covers — the node upsert, the order upsert and the node
 * delete — are the only places in the app where a failure LOSES the user's
 * work, and none of them was reachable by Sentry. They are not invokes, so the
 * reporter wired into `createInvokablePromise` never sees them; and the two
 * save paths deliberately swallow their rejection (PSTUDIO-3669 stopped a
 * rejected reorder from crashing the editor), which also closed the last route
 * to `unhandledrejection`. The result was a user watching "Autosave Error"
 * while Sentry recorded nothing at all.
 *
 * GUARDED, and that is the whole reason this is a function rather than three
 * inline calls: the toast beside it is what tells the user their work did not
 * save, and telemetry must never be able to take that away. The same shape as
 * the catch in `publish-bot-version.ts`.
 *
 * `extra` carries identifiers only. The error's own message can carry backend
 * text — `checkForErrors` prefers the server's over the generic one — but row
 * content never travels: no cell values, no node payloads.
 */
export const reportStoreFailure = (
  error: unknown,
  operation: 'autosave-nodes' | 'reorder-nodes' | 'delete-nodes',
  extra: Record<string, unknown> = {}
): void => {
  try {
    reportError(error, {
      source: `table-store:${operation}`,
      tags: { app: 'pro-studio', storeOperation: operation },
      extra,
    });
  } catch {
    // Deliberately empty. A reporter that throws must not break the path that
    // shows the user what went wrong.
  }
};
